// Admin configuration
// In production, these should be environment variables

export const ADMIN_CONFIG = {
  // Admin addresses that can access the admin panel
  // Replace with actual admin addresses
  adminAddresses: [
    process.env.REACT_APP_ADMIN_ADDRESS_1 || '0x0000000000000000000000000000000000000000',
    process.env.REACT_APP_ADMIN_ADDRESS_2,
    process.env.REACT_APP_ADMIN_ADDRESS_3,
  ].filter(Boolean), // Remove undefined values

  // Multi-sig configuration (if using)
  multiSig: {
    address: process.env.REACT_APP_MULTISIG_ADDRESS,
    requiredSignatures: 2,
  },

  // Feature flags
  features: {
    deployment: process.env.REACT_APP_ENABLE_DEPLOYMENT !== 'false',
    rebalancing: process.env.REACT_APP_ENABLE_REBALANCING !== 'false',
    emergencyControls: process.env.REACT_APP_ENABLE_EMERGENCY !== 'false',
    adapterManagement: process.env.REACT_APP_ENABLE_ADAPTER_MGMT !== 'false',
  },

  // Deployment defaults
  deploymentDefaults: {
    depositCap: '1000000', // $1M USDC
    performanceFee: 1000, // 10% in basis points
    depositFee: 0, // 0%
    withdrawalFee: 0, // 0%
  },

  // Rebalancing constraints
  rebalancing: {
    minAllocation: 5, // Minimum 5% allocation per adapter
    maxAllocation: 80, // Maximum 80% allocation per adapter
    maxSlippage: 100, // 1% max slippage in basis points
  },

  // Emergency thresholds
  emergency: {
    maxDrawdown: 1000, // 10% max drawdown before alert
    minHealthScore: 80, // Minimum health score (out of 100)
    cooldownPeriod: 3600, // 1 hour cooldown between emergency actions
  },
};

// Protocol configurations
export const PROTOCOL_CONFIGS = {
  aave: {
    name: 'Aave V3',
    address: '0x82E64f49Ed5EC1bC6e43DAD4FC8Af9bb3A2312EE',
    aToken: '0x625E7708f30cA75bfd92586e17077590C60eb4cD',
    color: '#B6509E',
    riskLevel: 'low',
    maxAllocation: 80,
    description: 'Leading DeFi lending protocol',
  },
  silo: {
    name: 'Silo Finance',
    address: '', // To be determined
    shareToken: '',
    color: '#00D4FF',
    riskLevel: 'medium',
    maxAllocation: 50,
    description: 'Isolated lending markets',
  },
  fortyAcres: {
    name: '40 Acres Finance',
    address: '0x124D00b1ce4453Ffc5a5F65cE83aF13A7709baC7',
    vault: '0x124D00b1ce4453Ffc5a5F65cE83aF13A7709baC7',
    color: '#4CAF50',
    riskLevel: 'medium',
    maxAllocation: 40,
    description: 'Yield optimization vault',
  },
};

// Strategy presets
export const STRATEGY_PRESETS = {
  conservative: {
    name: 'Conservative',
    description: 'Low risk, stable returns',
    icon: '🛡️',
    allocations: {
      aave: 70,
      silo: 20,
      fortyAcres: 10,
    },
    targetAPY: '3-5%',
    riskScore: 2,
  },
  balanced: {
    name: 'Balanced',
    description: 'Balanced risk and reward',
    icon: '⚖️',
    allocations: {
      aave: 50,
      silo: 30,
      fortyAcres: 20,
    },
    targetAPY: '4-7%',
    riskScore: 5,
  },
  growth: {
    name: 'Growth',
    description: 'Higher risk, higher returns',
    icon: '📈',
    allocations: {
      aave: 30,
      silo: 40,
      fortyAcres: 30,
    },
    targetAPY: '6-10%',
    riskScore: 7,
  },
  aggressive: {
    name: 'Aggressive',
    description: 'Maximum yield seeking',
    icon: '🚀',
    allocations: {
      aave: 20,
      silo: 40,
      fortyAcres: 40,
    },
    targetAPY: '8-15%',
    riskScore: 9,
  },
};

// Validation helpers
export const validateAddress = (address) => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

export const validateAllocation = (allocations) => {
  const total = Object.values(allocations).reduce((sum, val) => sum + parseFloat(val || 0), 0);
  return Math.abs(total - 100) < 0.01; // Allow for small rounding errors
};

export const isAdmin = (address) => {
  if (!address) return false;
  return ADMIN_CONFIG.adminAddresses
    .map(a => a?.toLowerCase())
    .includes(address.toLowerCase());
};

// Risk assessment
export const calculateRiskScore = (allocations) => {
  let score = 0;
  let totalWeight = 0;

  Object.entries(allocations).forEach(([protocol, percentage]) => {
    const config = PROTOCOL_CONFIGS[protocol];
    if (config) {
      const riskMultiplier = 
        config.riskLevel === 'low' ? 1 :
        config.riskLevel === 'medium' ? 5 :
        config.riskLevel === 'high' ? 9 : 5;
      
      score += (percentage / 100) * riskMultiplier;
      totalWeight += percentage / 100;
    }
  });

  return totalWeight > 0 ? Math.round(score / totalWeight) : 0;
};

// APY estimation based on allocations
export const estimateAPY = (allocations, protocolAPYs) => {
  let weightedAPY = 0;
  
  Object.entries(allocations).forEach(([protocol, percentage]) => {
    const apy = protocolAPYs[protocol] || 0;
    weightedAPY += (percentage / 100) * apy;
  });
  
  return weightedAPY;
};

// Format allocation for display
export const formatAllocation = (allocations) => {
  return Object.entries(allocations)
    .map(([protocol, percentage]) => {
      const config = PROTOCOL_CONFIGS[protocol];
      return `${config?.name || protocol}: ${percentage}%`;
    })
    .join(', ');
};

// Export deployment script generator
export const generateDeploymentScript = (config) => {
  const {
    vaultName,
    vaultSymbol,
    depositCap,
    feeRecipient,
    allocations,
  } = config;

  return {
    vault: {
      name: vaultName,
      symbol: vaultSymbol,
      asset: '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E', // USDC
      feeRecipient: feeRecipient || ADMIN_CONFIG.adminAddresses[0],
      depositCap: parseFloat(depositCap) * 1e6, // Convert to USDC decimals
    },
    adapters: Object.entries(allocations).map(([protocol, allocation]) => ({
      protocol,
      allocation,
      config: PROTOCOL_CONFIGS[protocol],
    })),
    verification: {
      totalAllocation: Object.values(allocations).reduce((sum, val) => sum + val, 0),
      riskScore: calculateRiskScore(allocations),
    },
  };
};